import { type ISODate, addDays, daysBetween, toISODate, weekday } from './dates';
import { capitalize, normalize } from './format';
import type { Priority } from './types';

/**
 * Captura em linguagem natural: "Revisar contrato sexta #jurídico @ana +compliance &fundoX !alta".
 * Cada marcador reconhecido vira um campo e sai do título.
 */
export interface CaptureContext {
  today: ISODate;
  projects: { id: string; name: string }[];
  themes: { id: string; name: string }[];
  people: { id: string; name: string }[];
  entities: { id: string; name: string; short?: string }[];
}

export type CaptureToken =
  | { kind: 'project'; id: string; label: string }
  | { kind: 'person'; id: string; label: string }
  | { kind: 'entity'; id: string; label: string }
  | { kind: 'theme'; id: string; label: string }
  | { kind: 'priority'; value: Priority }
  | { kind: 'due'; date: ISODate };

export interface CaptureResult {
  title: string;
  due: ISODate | null;
  priority: Priority;
  assigneeId: string | null;
  projectId: string | null;
  themeIds: string[];
  entityIds: string[];
  tokens: CaptureToken[];
}

const matchesPrefix = (name: string, q: string, minWord = 1) => {
  const n = normalize(name);
  return n.startsWith(q) || n.split(/\s+/).some(w => w.length > minWord && w.startsWith(q));
};

export const findProject = (ctx: CaptureContext, p: string) => ctx.projects.find(x => matchesPrefix(x.name, normalize(p)));
export const findTheme = (ctx: CaptureContext, p: string) => ctx.themes.find(x => matchesPrefix(x.name, normalize(p)));
export function findPerson(ctx: CaptureContext, p: string) {
  const q = normalize(p);
  return ctx.people.find(x => {
    const n = normalize(x.name);
    return n && (n.startsWith(q) || n.split(/\s+/).some(w => w.startsWith(q)));
  });
}
export function findEntity(ctx: CaptureContext, p: string) {
  const q = normalize(p);
  return ctx.entities.find(n => {
    const a = normalize(n.short || ''), b = normalize(n.name);
    return a.startsWith(q) || b.startsWith(q) || b.split(/\s+/).some(w => w.length > 2 && w.startsWith(q));
  });
}

const WEEKDAY_KEYS = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];

type DateRule = [RegExp, (m: RegExpMatchArray, today: ISODate) => ISODate | ''];
const DATE_RULES: DateRule[] = [
  [/\s((?:at[ée] )?depois de amanh[ãa])(?=\s)/i, (_, t) => addDays(t, 2)],
  [/\s((?:at[ée] )?amanh[ãa])(?=\s)/i, (_, t) => addDays(t, 1)],
  [/\s((?:at[ée] )?hoje)(?=\s)/i, (_, t) => t],
  [/\s((?:na )?semana que vem)(?=\s)/i, (_, t) => addDays(t, 7)],
  [/\s(em (\d{1,2}) dias?)(?=\s)/i, (m, t) => addDays(t, Number(m[2]))],
  [/\s((?:at[ée] )?(?:dia )?(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?)(?=\s)/i, (m, t) => {
    const thisYear = Number(t.slice(0, 4));
    const year = m[4] ? (m[4].length === 2 ? 2000 + Number(m[4]) : Number(m[4])) : thisYear;
    const dt = new Date(year, Number(m[3]) - 1, Number(m[2]));
    if (Number.isNaN(dt.getTime()) || dt.getDate() !== Number(m[2])) return '';
    if (!m[4] && toISODate(dt) < t) dt.setFullYear(year + 1);
    return toISODate(dt);
  }],
  [/\s((?:na |no |at[ée] )?(segunda|ter[çc]a|quarta|quinta|sexta|s[áa]bado|domingo)(?:-feira)?)(?=\s)/i, (m, t) => {
    const target = WEEKDAY_KEYS.indexOf(normalize(m[2]));
    const n = (target - weekday(t) + 7) % 7 || 7;
    return addDays(t, n);
  }],
];

export function parseCapture(text: string, ctx: CaptureContext): CaptureResult {
  let t = ' ' + text + ' ';
  const r: CaptureResult = { title: '', due: null, priority: 0, assigneeId: null, projectId: null, themeIds: [], entityIds: [], tokens: [] };

  t = t.replace(/\s#([\wÀ-ÿ-]+)/g, (m, p: string) => {
    const pr = findProject(ctx, p);
    if (pr && !r.projectId) { r.projectId = pr.id; r.tokens.push({ kind: 'project', id: pr.id, label: pr.name }); return ' '; }
    return m;
  });
  t = t.replace(/\s@([\wÀ-ÿ]+)/g, (m, p: string) => {
    const pe = findPerson(ctx, p);
    if (pe && !r.assigneeId) { r.assigneeId = pe.id; r.tokens.push({ kind: 'person', id: pe.id, label: pe.name }); return ' '; }
    return m;
  });
  t = t.replace(/\s&([\wÀ-ÿ-]+)/g, (m, p: string) => {
    const en = findEntity(ctx, p);
    if (en && !r.entityIds.includes(en.id)) { r.entityIds.push(en.id); r.tokens.push({ kind: 'entity', id: en.id, label: en.short || en.name }); return ' '; }
    return m;
  });
  t = t.replace(/\s\+([\wÀ-ÿ-]+)/g, (m, p: string) => {
    const th = findTheme(ctx, p);
    if (th && !r.themeIds.includes(th.id)) { r.themeIds.push(th.id); r.tokens.push({ kind: 'theme', id: th.id, label: th.name }); return ' '; }
    return m;
  });
  t = t.replace(/\s!(alta|m[eé]dia|baixa|!{0,2})(?=\s)/gi, (_m, p: string) => {
    const v = normalize(p);
    const n = (v === 'alta' ? 3 : v === 'media' ? 2 : v === 'baixa' ? 1 : p.length + 1) as Priority;
    r.priority = n;
    r.tokens.push({ kind: 'priority', value: n });
    return ' ';
  });
  for (const [re, f] of DATE_RULES) {
    const m = t.match(re);
    if (!m) continue;
    const v = f(m, ctx.today);
    if (v) { r.due = v; r.tokens.push({ kind: 'due', date: v }); t = t.replace(m[0], ' '); break; }
  }
  r.title = capitalize(t.replace(/\s+/g, ' ').trim());
  return r;
}

/** Texto curto para exibir um prazo reconhecido. */
export const isNearDay = (date: ISODate, today: ISODate) => Math.abs(daysBetween(today, date)) <= 1;
